import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { ElectionSummary } from '../types';
import { ArrowRight, UserCheck, ShieldCheck, Clock, CheckCircle2 } from 'lucide-react';
import { setSiteMeta } from '../utils/meta';

export const VoterDetailsPage: React.FC = () => {
  const { voter } = useAuth();
  const navigate = useNavigate();

  const [electionInfo, setElectionInfo] = useState<ElectionSummary | null>(null);
  const [totalVoted, setTotalVoted] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!voter) {
      navigate('/');
      return;
    }

    const currentSync = api.getElectionSettingsSync();
    setSiteMeta({ election: currentSync, pageTitle: 'Voter Accreditation & Bio' });

    // Fetch total ballots casted for this election
    const electionId = (voter as any)?.election_id || (voter as any)?.electionId;
    api.getTotalVoted(electionId).then((count) => {
      setTotalVoted(count);
    });

    // Verify election schedule window & fetch info
    api.getElectionInfo().then((info) => {
      setElectionInfo(info);
      setSiteMeta({ election: info, pageTitle: 'Voter Accreditation & Bio' });
      if (info.id && !electionId) {
        api.getTotalVoted(info.id).then((count) => {
          setTotalVoted(count);
        });
      }
      const now = new Date();
      if (info.startTime && now < new Date(info.startTime)) {
        navigate('/error/votingnotstarted');
      } else if (info.endTime && now > new Date(info.endTime)) {
        navigate('/error/votingended');
      } else if (info.status === 'paused' || info.status === 'draft') {
        navigate('/error/votingnotstarted');
      }
    });
  }, [voter, navigate]);

  // Live countdown timer to deadline
  useEffect(() => {
    if (!electionInfo?.endTime) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const end = new Date(electionInfo.endTime).getTime();
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [electionInfo?.endTime]);

  if (!voter) return null;

  return (
    <div className="dashboard-layout" style={{ minHeight: '100vh', backgroundColor: '#FFFFFF' }}>
      <Sidebar />

      {/* Main Content Pane */}
      <main className="dashboard-main-content">
        <div className="voter-welcome-container">
          <div className="accreditation-label">
            Accreditation Verification
          </div>
          <h1 className="voter-welcome-title">
            Welcome, <span className="voter-welcome-name">{voter.name}</span>
          </h1>
          <p className="voter-welcome-desc">
            Please confirm your registered academic information below before opening your ballot.
          </p>
        </div>

        {/* Legacy-Parity Info Box: Election Deadline Countdown & Total Votes Casted */}
        <div className="cd-infobox">
          <div className="cd-infobox-grid">
            <div className="election-deadline-block">
              <div className="cd-infobox-title">
                <Clock size={18} color="#FF9900" />
                <span>Election Deadline</span>
              </div>
              <div className="e-deadline-boxes">
                <div className="deadline-unit-box">
                  <h3>{String(timeLeft.days).padStart(2, '0')}</h3>
                  <p>Days</p>
                </div>
                <div className="deadline-unit-box">
                  <h3>{String(timeLeft.hours).padStart(2, '0')}</h3>
                  <p>Hours</p>
                </div>
                <div className="deadline-unit-box">
                  <h3>{String(timeLeft.minutes).padStart(2, '0')}</h3>
                  <p>Mins</p>
                </div>
                <div className="deadline-unit-box">
                  <h3>{String(timeLeft.seconds).padStart(2, '0')}</h3>
                  <p>Secs</p>
                </div>
              </div>
            </div>

            <div className="total-votes-block">
              <div className="cd-infobox-title">
                <CheckCircle2 size={18} color="#FF9900" />
                <span>Total Votes Casted</span>
              </div>
              <div className="t-votes-box">
                <h3>
                  {totalVoted !== null
                    ? totalVoted.toLocaleString()
                    : electionInfo?.totalVoted !== undefined
                    ? electionInfo.totalVoted.toLocaleString()
                    : '0'}
                </h3>
                <p>Ballots Casted</p>
              </div>
            </div>
          </div>
        </div>

        {/* Voter Details Card */}
        <div
          className="cv-post-card voter-record-card"
          style={{
            backgroundColor: '#171619',
            color: '#FFFFFF',
            boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
            marginBottom: '32px',
            border: '1px solid #2D2C31',
          }}
        >
          <div className="voter-record-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #2D2C31', paddingBottom: '16px' }}>
            <ShieldCheck size={22} color="#FF9900" style={{ flexShrink: 0 }} />
            <h2 className="voter-record-heading" style={{ fontSize: '17px', fontWeight: 700, margin: 0 }}>
              Voter Record & Ballot Eligibility
            </h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
            }}
          >
            <div>
              <div style={{ color: '#8E8E93', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                Full Name
              </div>
              <div style={{ fontSize: '17px', fontWeight: 600, wordBreak: 'break-word' }}>{voter.name}</div>
            </div>

            <div>
              <div style={{ color: '#8E8E93', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                Matric Number
              </div>
              <div style={{ fontSize: '17px', fontWeight: 600, color: '#FF9900' }}>{voter.matric}</div>
            </div>

            <div>
              <div style={{ color: '#8E8E93', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                Email Address
              </div>
              <div style={{ fontSize: '15px', fontWeight: 500, wordBreak: 'break-all' }}>{voter.email}</div>
            </div>

            <div>
              <div style={{ color: '#8E8E93', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                Academic Level
              </div>
              <div style={{ fontSize: '17px', fontWeight: 600 }}>{voter.level} Level</div>
            </div>

            <div>
              <div style={{ color: '#8E8E93', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                Hall of Residence
              </div>
              <div style={{ fontSize: '15px', fontWeight: 500 }}>{voter.hall || 'Independence Hall'}</div>
            </div>

            <div>
              <div style={{ color: '#8E8E93', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                Department
              </div>
              <div style={{ fontSize: '15px', fontWeight: 500 }}>{voter.department || 'Faculty of Law'}</div>
            </div>
          </div>

          <div style={{ marginTop: '28px', padding: '14px', backgroundColor: '#1E1D21', borderRadius: '12px', fontSize: '13px', color: '#A1A1AA', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <UserCheck size={20} color="#10B981" style={{ flexShrink: 0 }} />
            <span>
              Your voter token is cryptographically verified. Once you submit your vote, it cannot be edited or recast.
            </span>
          </div>
        </div>

        {/* CTA Button */}
        <div>
          <button
            onClick={() => navigate('/vote/cast-vote')}
            className="btn-primary"
            style={{ maxWidth: '320px', width: '100%', height: '54px', fontSize: '16px' }}
          >
            <span>Proceed to Ballot</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </main>
    </div>
  );
};
